import { Component, EventEmitter, Input, OnInit, OnChanges, Output, ViewChild } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { InspectionService } from '../../../inspection.service';
import { dateTimeAsString, lastCopySavedAt } from '../../../../shared/date.helper';
import { Inspection, SentEmail, UnderwritingStatus } from '../../../../shared/interfaces';
import { ViewEmailModalComponent } from '../../../../shared/_components/view-email-modal/view-email-modal.component';
import { SendEmailModalComponent } from '../../../../shared/_components/send-email-modal/send-email-modal.component';
import { FlagManagerService } from '../../flag-manager.service';

@Component({
  selector: 'app-letter-history-panel',
  templateUrl: './letter-history-panel.component.html',
  styleUrls: ['./letter-history-panel.component.scss'],
})
export class LetterHistoryPanelComponent implements OnInit, OnChanges {
  @Input() inspection: Inspection;
  @Input() isVisible: boolean = false;
  @Output() closePanel = new EventEmitter<void>();
  @ViewChild('sendEmailModal', { static: false }) public sendEmailModal: SendEmailModalComponent;

  existingLetters: any[] = [];
  customerUrl: string;
  letterId: number;
  dateTimeAsString = dateTimeAsString;
  lastCopySavedAt = lastCopySavedAt;
  isLoadingSendEmail: boolean = false;
  showSendEmailModal: boolean = false;

  isNoIssuesLetter(letter: any): boolean {
    return letter?.data?.template === 'no-issues';
  }

  constructor(
    private inspectionService: InspectionService,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private flagManagerService: FlagManagerService,
  ) {}

  ngOnInit(): void {
    if (this.inspection) {
      this.setCustomerUrl();
      this.loadLetters();
    }
  }

  ngOnChanges(): void {
    if (this.inspection && this.isVisible) {
      this.setCustomerUrl();
      this.loadLetters();
    }
  }

  private setCustomerUrl(): void {
    if (this.inspection && this.inspection.hash) {
      this.customerUrl = location.origin + '/recommendation-letter/' + encodeURIComponent(this.inspection.hash);
    } else {
      this.customerUrl = undefined as any;
    }
  }

  private async loadLetters(): Promise<void> {
    this.existingLetters = await this.flagManagerService.loadLetters(this.inspection.id);
  }

  openLetter(letter: any): void {
    window.open(`${this.customerUrl}?version=${letter.id}`, 'Recommendation Letter');
  }

  openLatestLetter(): void {
    if (this.existingLetters && this.existingLetters.length > 0) {
      window.open(`${this.customerUrl}?skipRegisteringView=true`, 'Recommendation Letter');
    }
  }

  copy(url: string): void {
    navigator.clipboard.writeText(url);
    this.toastr.success('Customer URL copied to clipboard!');
  }

  previewEmail(letterEmail: SentEmail): void {
    this.modalService.show(ViewEmailModalComponent, {
      class: 'modal-lg',
      initialState: {
        letterEmail: letterEmail,
        productType: this.inspection.policy.productType,
      },
    });
  }

  async downloadEmail(letterEmail: SentEmail): Promise<void> {
    try {
      const data = await this.inspectionService.downloadSentEmail(letterEmail.id);
      const blob = new Blob([data as any], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'RecLetterEmail.pdf';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading email:', error);
      this.toastr.error('Failed to download email');
    }
  }

  onClosePanel(): void {
    this.closePanel.emit();
  }

  openSendEmail(letterId: number): void {
    this.letterId = letterId;
    this.showSendEmailModal = true;
    this.sendEmailModal.show();
  }

  closeSendEmailModal(): void {
    this.showSendEmailModal = false;
  }

  async handleEmailSent(sentEmail: any): Promise<void> {
    await this.loadLetters();
    this.closeSendEmailModal();

    // Update status to 'Email Sent' when email is sent
    if (this.inspection?.id) {
      try {
        if (this.inspection.underwritingStatus < UnderwritingStatus.LETTER_SENT) {
          await this.flagManagerService.updateUnderwritingStatus(this.inspection.id, UnderwritingStatus.LETTER_SENT);
        }
      } catch (error) {
        console.error('Error updating status to email sent:', error);
      }
    }
  }
}
